import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <motion.header
        className="sticky top-0 z-50 flex items-center justify-between bg-background-50/80 px-4 py-3 backdrop-blur-md"
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
      >
        <Link
          to="/"
          className="flex items-center gap-1.5 text-sm text-foreground-400 transition-colors hover:text-foreground-200 whitespace-nowrap cursor-pointer"
        >
          <span className="flex h-7 w-7 items-center justify-center">
            <i className="ri-arrow-left-line text-lg" />
          </span>
          <span className="hidden sm:inline">トップページ</span>
        </Link>
        <span className="text-sm font-bold text-foreground-100">プライバシーポリシー</span>
        <div className="w-20" />
      </motion.header>

      {/* Content */}
      <main className="mx-auto max-w-2xl px-4 py-8 pb-32 sm:px-6 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h1 className="font-heading text-2xl font-bold text-foreground-100 sm:text-3xl">
            プライバシーポリシー
          </h1>
          <p className="mt-2 text-sm text-foreground-400">
            最終更新日：2025年1月1日
          </p>

          <div className="mt-8 space-y-8 text-sm leading-relaxed text-foreground-300 sm:text-base">
            <section>
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-100">
                はじめに
              </h2>
              <p>
                Sparkle LIVE（以下「当社」といいます）は、ユーザーの皆さまの個人情報の重要性を深く認識し、個人情報の保護に関する法律（個人情報保護法）その他の関連法令を遵守するとともに、以下のプライバシーポリシー（以下「本ポリシー」といいます）に従って適切な取扱いと保護に努めます。
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-100">
                1. 収集する情報
              </h2>
              <p className="mb-2">
                当社は、本サービスの提供にあたり、以下の情報を収集することがあります：
              </p>
              <ol className="list-decimal space-y-2 pl-5">
                <li>
                  <strong className="text-foreground-100">アカウント情報：</strong>
                  メールアドレス、電話番号、生年月日、性別、ニックネーム、プロフィール写真など、アカウント登録時にご提供いただく情報
                </li>
                <li>
                  <strong className="text-foreground-100">プロフィール情報：</strong>
                  自己紹介文、趣味・興味、居住地域、身長、体型など、ユーザーが任意で設定する情報
                </li>
                <li>
                  <strong className="text-foreground-100">位置情報：</strong>
                  マッチング機能の提供のため、ユーザーのおおまかな位置情報（GPSデータ）を収集します。位置情報の利用は、端末の設定からいつでも無効にできます。
                </li>
                <li>
                  <strong className="text-foreground-100">利用状況情報：</strong>
                  アプリの利用履歴、ログイン時間、閲覧履歴、マッチング履歴、メッセージの送受信状況など
                </li>
                <li>
                  <strong className="text-foreground-100">端末情報：</strong>
                  デバイスの種類、OS、IPアドレス、広告識別子（IDFA/AAID）、ブラウザ情報など
                </li>
                <li>
                  <strong className="text-foreground-100">決済情報：</strong>
                  有料サービスのご利用時に、決済処理に必要な情報（クレジットカード情報は決済代行会社が管理し、当社は保持しません）
                </li>
                <li>
                  <strong className="text-foreground-100">お問い合わせ情報：</strong>
                  お問い合わせやカスタマーサポートの際にご提供いただく情報
                </li>
              </ol>
            </section>

            <section>
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-100">
                2. 情報の利用目的
              </h2>
              <p className="mb-2">
                当社は、収集した情報を以下の目的で利用します：
              </p>
              <ol className="list-decimal space-y-2 pl-5">
                <li>本サービスの提供・運営・改善のため</li>
                <li>ユーザー認証および本人確認のため</li>
                <li>マッチング機能の提供および最適化のため</li>
                <li>ユーザーサポートおよびお問い合わせ対応のため</li>
                <li>利用規約違反の監視・防止・対応のため</li>
                <li>本サービスに関する重要なお知らせの配信のため</li>
                <li>キャンペーン・アンケート等の実施のため</li>
                <li>マーケティング分析および新サービス開発のため</li>
                <li>第三者への広告配信（パーソナライズド広告を含む）のため</li>
              </ol>
            </section>

            <section>
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-100">
                3. 情報の第三者提供
              </h2>
              <p>
                当社は、以下の場合を除き、ユーザーの個人情報を第三者に提供することはありません：
              </p>
              <ol className="mt-2 list-decimal space-y-2 pl-5">
                <li>ユーザー本人の同意がある場合</li>
                <li>法令に基づく場合</li>
                <li>
                  人の生命、身体または財産の保護のために必要がある場合であって、本人の同意を得ることが困難であるとき
                </li>
                <li>公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合</li>
                <li>
                  国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合
                </li>
                <li>
                  業務委託先に対し、利用目的の達成に必要な範囲内で提供する場合（この場合、当社は委託先に対し適切な監督を行います）
                </li>
              </ol>
            </section>

            <section>
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-100">
                4. Cookie・広告識別子
              </h2>
              <p>
                本サービスでは、ユーザー体験の向上および広告配信の最適化のため、Cookieおよび広告識別子（IDFA/AAID）を使用しています。ユーザーは、端末の設定から広告トラッキングの制限やCookieの無効化を行うことができます。ただし、これにより一部の機能が正常に動作しなくなる可能性があります。
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-100">
                5. 情報の保存期間
              </h2>
              <p>
                当社は、利用目的に必要な期間に限り個人情報を保存します。アカウントが削除された場合、法令に基づく保存義務がある場合を除き、合理的な期間内に当該ユーザーの個人情報を削除または匿名化します。
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-100">
                6. セキュリティ
              </h2>
              <p>
                当社は、ユーザーの個人情報を保護するため、技術的・組織的な安全対策を講じています。具体的には、データの暗号化、アクセス制限、ファイアウォールの設置、定期的なセキュリティ監査等を実施しています。ただし、インターネット上の通信において完全な安全性を保証することはできません。
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-100">
                7. ユーザーの権利
              </h2>
              <p className="mb-2">
                ユーザーは、当社が保有する自己の個人情報について、以下の権利を有します：
              </p>
              <ol className="list-decimal space-y-2 pl-5">
                <li>開示請求権：当社が保有する自己の個人情報の開示を請求することができます。</li>
                <li>訂正請求権：個人情報が事実と異なる場合、訂正・追加・削除を請求することができます。</li>
                <li>利用停止請求権：個人情報の利用停止または消去を請求することができます。</li>
                <li>第三者提供停止請求権：個人情報の第三者提供の停止を請求することができます。</li>
              </ol>
            </section>

            <section>
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-100">
                8. 未成年者のプライバシー
              </h2>
              <p>
                本サービスは18歳以上の方を対象としています。18歳未満の方の個人情報を意図的に収集することはありません。18歳未満の方の個人情報を収集したことが判明した場合、速やかに当該情報を削除します。
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-100">
                9. プライバシーポリシーの変更
              </h2>
              <p>
                当社は、必要に応じて本ポリシーを改定することがあります。重要な変更がある場合は、本サービス上での告知またはメールにてお知らせします。変更後も本サービスを継続して利用する場合、変更後のポリシーに同意したものとみなします。
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-100">
                お問い合わせ
              </h2>
              <p>
                本ポリシーに関するお問い合わせ、または個人情報の開示・訂正・利用停止等のご請求は、以下までご連絡ください：
              </p>
              <p className="mt-2 text-primary-400">support@sparkle-live.app</p>
            </section>
          </div>
        </motion.div>
      </main>

      {/* Fixed bottom CTA */}
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-background-200/50 bg-background-50/95 px-4 py-3 backdrop-blur-md"
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.4, ease: [0.25, 0.4, 0.25, 1] }}
      >
        <div className="mx-auto max-w-2xl">
          <a
            href="/#download"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-download-line" />
            アプリを今すぐダウンロード
          </a>
        </div>
      </motion.div>
    </div>
  );
}